import {
	DispatchFunction,
	ActionType,
	IndividualStateKey,
	IndividualState,
	FieldStateKey,
	Brands,
	ScreenIndex,
	SelectedData,
	OcrProcessingData,
	ScreenIndexPREMIER,
	ScreenIndexOPTIMA,
	ScreenIndexMultinational,
	ScreenIndexSODA,
} from '../../context/interfaces';
import { BRAND } from 'shared/utils';
import brandModels from './../../static/constants/brand_models.json';

const selectedSexOptions = [
	{ id: '', name: null },
	{ id: '1', name: 'Femenino' },
	{ id: '2', name: 'Masculino' },
	{ id: '-1', name: 'N / D' },
];

const selectedDocumentTypeOptions = [
	{ id: '', name: null },
	{ id: 'CÉDULA', name: 'Cédula de ciudadanía' },
	{ id: 'EXTRANJERO', name: 'Cédula de extranjería' },
	{ id: 'PASAPORTE', name: 'Pasaporte' },
	{ id: 'NIT', name: 'NIT' },
];

const selectedLinkType = [
	{ id: '', name: null },
	{ id: 'familiar', name: 'Familiar' },
	{ id: 'comercial', name: 'Comercial' },
	{ id: 'laboral', name: 'Laboral' },
	{ id: 'otro', name: 'Otro' },
];

const getBrandsCatalog = (): SelectedData[] => {
	return Object.keys(brandModels).map((brand) => ({ id: brand, name: brand }));
};

const getModelCatalog = (): SelectedData[] => {
	const currentBrand = sessionStorage.getItem('brandSelectedForClient');

	if (!currentBrand) {
		return [];
	}

	const results: any = brandModels[currentBrand as keyof typeof brandModels] || [];

	return results.map((model: any) => ({ id: model, name: model }));
};

// Taken from: https://stackoverflow.com/questions/46155/how-to-validate-an-email-address-in-javascript
export const validateEmail = (email: string): boolean => {
	const re =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
	return re.test(String(email).toLowerCase().trim());
};

export const isNum = (value: string): boolean => {
	return /^\d+$/.test(value);
};

export const buildFieldOnChange = (
	dispatch: DispatchFunction,
	stateKey: IndividualStateKey,
	fieldStateKey: FieldStateKey
) => {
	return ({ target }: any): void => {
		const value: any = target.type === 'checkbox' ? target.checked : target.value;
		dispatch({
			type: ActionType.SET_FIELD_VALUE,
			data: { stateKey, fieldStateKey, value },
		});

		if (fieldStateKey === FieldStateKey.MAKE) {
			sessionStorage.setItem('brandSelectedForClient', value);
		}

		// Validate OCR Data
		const ocrDataStr = localStorage.getItem('ocrData') ?? null;
		const ocrStorage: OcrProcessingData = ocrDataStr ? JSON.parse(ocrDataStr) : null;
		if (ocrStorage) {
			if (fieldStateKey === FieldStateKey.LICENSE) {
				ocrStorage.driverLicense = value;
			} else if (fieldStateKey === FieldStateKey.FIRST_NAME) {
				let fullName = ocrStorage.fullName;
				if (ocrStorage.fullName && ocrStorage.fullName.length > 0) {
					let fullNameParts = ocrStorage.fullName?.split(' ');
					if (ocrStorage.fullName.indexOf(',') > -1) {
						fullNameParts = ocrStorage.fullName?.split(',');
						fullName = fullNameParts[0] + ', ' + value;
					} else {
						fullName = value + ' ' + fullNameParts[1];
					}
				}

				ocrStorage.fullName = fullName;
			} else if (fieldStateKey === FieldStateKey.LAST_NAME) {
				let fullName = ocrStorage.fullName;
				if (ocrStorage.fullName && ocrStorage.fullName.length > 0) {
					let fullNameParts = ocrStorage.fullName?.split(' ');
					if (ocrStorage.fullName.indexOf(',') > -1) {
						fullNameParts = ocrStorage.fullName?.split(',');
						fullName = value + ', ' + fullNameParts[1];
					} else {
						fullName = fullNameParts[0] + ' ' + value;
					}
				}

				ocrStorage.fullName = fullName;
			} else if (fieldStateKey === FieldStateKey.PLATE) {
				ocrStorage.licensePlate = value;
			} else if (fieldStateKey === FieldStateKey.MAKE) {
				ocrStorage.make = value;
			} else if (fieldStateKey === FieldStateKey.MODEL) {
				ocrStorage.model = value;
			} else if (fieldStateKey === FieldStateKey.YEAR) {
				ocrStorage.year = value;
			} else if (fieldStateKey === FieldStateKey.COLOR) {
				ocrStorage.color = value;
			}

			localStorage.setItem('ocrData', JSON.stringify(ocrStorage));
			dispatch({ type: ActionType.SET_OCR_DATA_BY_THE_CLIENT, data: ocrStorage });
		}
	};
};

export const buildFieldOnClick = (
	dispatch: DispatchFunction,
	stateKey: IndividualStateKey,
	fieldStateKey: FieldStateKey
) => {
	return (value: any): void => {
		dispatch({
			type: ActionType.SET_FIELD_VALUE,
			data: { stateKey, fieldStateKey, value },
		});
	};
};

export interface Field {
	fieldKey: FieldStateKey;
	label: string;
	type: string;
	inputMode?: any;
	inputSize?: number;
	readonly: boolean;
	required: boolean;
	isHidden?: boolean;
	dependencyKey?: FieldStateKey;
	stateSelector: (state: IndividualState) => any;
	onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	isValid: (value: string) => boolean; // this is orthogonal to 'required'
	errorMessage: string;
	selectedData?: SelectedData[];
}

/**
 * This function is used to assign data to the form
 * @param dispatch
 * @param screenKey
 * @param ClaimantType
 * @param formData
 */
export const assignDataToForm = (
	dispatch: DispatchFunction,
	screenKey: ScreenIndex,
	ClaimantType: any,
	formData: IndividualState
): void => {
	const screenIndexMap = {
		[Brands.PREMIER]: {
			DRIVER: ScreenIndexPREMIER.DRIVER,
		},
		[Brands.OPTIMA]: {
			DRIVER: ScreenIndexOPTIMA.DRIVER,
		},
		[Brands.MULTINATIONAL]: {
			DRIVER: ScreenIndexMultinational.DRIVER,
		},
		[Brands.SODA]: {
			DRIVER: ScreenIndexSODA.DRIVER,
		},
	};

	const useCPE = BRAND in screenIndexMap;
	const screenIndexDriver = useCPE ? screenIndexMap[BRAND as keyof typeof screenIndexMap].DRIVER : ScreenIndex.DRIVER;

	if (screenKey === screenIndexDriver) {
		const formObj = formData.fields;
		const isDriver = formData.fields.isOtherDriver;

		const dataDriver = [
			{ fsKey: FieldStateKey.DRIVER_NAME.toString(), value: isDriver ? formObj.driverName : formObj?.firstName },
			{ fsKey: FieldStateKey.DRIVER_LASTNAME.toString(), value: isDriver ? formObj.driverLastName : formObj?.lastName },
			{ fsKey: FieldStateKey.DRIVER_SEX.toString(), value: isDriver ? formObj.driverSex : formObj?.sex },
			{ fsKey: FieldStateKey.DRIVER_LICENSE.toString(), value: isDriver ? formObj.driverLicense : formObj?.license },
			{
				fsKey: FieldStateKey.DRIVER_URBANIZATION.toString(),
				value: isDriver ? formObj.driverUrbanization : formObj?.urbanization,
			},
			{
				fsKey: FieldStateKey.DRIVER_POSTALCODE.toString(),
				value: isDriver ? formObj.driverPostalCode : formObj?.postalCode,
			},
			{ fsKey: FieldStateKey.DRIVER_PHONE.toString(), value: isDriver ? formObj.driverPhone : formObj?.phone },
			{ fsKey: FieldStateKey.DRIVER_EMAIL.toString(), value: isDriver ? formObj.driverEmail : formObj?.email },
		];
		dataDriver.map((m) => {
			switch (BRAND) {
				case Brands.CSM:
					dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: IndividualStateKey.INJURED, fieldStateKey: m.fsKey, value: m.value },
					});
					break;
				case Brands.MULTINATIONAL:
					dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: {
							stateKey:
								ClaimantType === 'Asegurado' || ClaimantType?.toUpperCase() === 'INSURED'
									? IndividualStateKey.INSURED
									: IndividualStateKey.INJURED,
							fieldStateKey: m.fsKey,
							value: m.value,
						},
					});
					break;
				case Brands.SODA:
				case Brands.PREMIER:
					dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: {
							stateKey:
								ClaimantType === 'Asegurado' || ClaimantType?.toUpperCase() === 'INSURED'
									? IndividualStateKey.INSURED
									: IndividualStateKey.INJURED,
							fieldStateKey: m.fsKey,
							value: m.value,
						},
					});
					break;
				case Brands.OPTIMA:
					dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: {
							stateKey:
								ClaimantType === 'Asegurado' || ClaimantType?.toUpperCase() === 'INSURED'
									? IndividualStateKey.INSURED
									: IndividualStateKey.INJURED,
							fieldStateKey: m.fsKey,
							value: m.value,
						},
					});
					break;
				default:
					dispatch({
						type: ActionType.SET_FIELD_VALUE,
						data: { stateKey: IndividualStateKey.INSURED, fieldStateKey: m.fsKey, value: m.value },
					});
					break;
			}
			return true;
		});
	}
};

/**
 * This function we expect to handle the dynamic calculation for what fields
 * to show given :individual, but in the future could be based on the company,
 * country, etc...
 * @param dispatch
 */
export const buildFields = (
	dispatch: DispatchFunction,
	stateKey: IndividualStateKey,
	screenKey: ScreenIndex,
	SinisterType: string | undefined
): Field[] => {
	const name = {
		fieldKey: FieldStateKey.NAME,
		label: [Brands.QUALITAS, Brands.INS].includes(BRAND as Brands)
			? 'Nombre y Apellidos (Opcional)'
			: 'Nombre y Apellidos',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.name,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.NAME),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Nombre y Apellidos requerido',
		inputSize: 100,
	};
	const firstName = {
		fieldKey: FieldStateKey.FIRST_NAME,
		label: 'Nombre',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.firstName,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.FIRST_NAME),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Nombre requerido',
	};
	const lastName = {
		fieldKey: FieldStateKey.LAST_NAME,
		label: 'Apellidos',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.lastName,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.LAST_NAME),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Apellidos requeridos',
	};
	const phone = {
		fieldKey: FieldStateKey.PHONE,
		label: [Brands.QUALITAS, Brands.INS].includes(BRAND as Brands) ? 'Teléfono (Opcional)' : 'Teléfono',
		type: 'phone',
		inputMode: 'numeric',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.phone,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.PHONE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Teléfono requerido',
		inputSize: 20,
	};
	const plate = {
		fieldKey: FieldStateKey.PLATE,
		label: [Brands.QUALITAS, Brands.INS].includes(BRAND as Brands) ? 'Placa (Opcional)' : 'Placa',
		type: 'text',
		readonly: stateKey === IndividualStateKey.INSURED,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.plate,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.PLATE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Placa requerida',
		inputSize: 15,
	};
	const make = {
		fieldKey: FieldStateKey.MAKE,
		label: [Brands.QUALITAS, Brands.INS].includes(BRAND as Brands) ? 'Marca (Opcional)' : 'Marca',
		type: 'text',
		readonly: stateKey === IndividualStateKey.INSURED,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.make,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.MAKE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Marca requerida',
		inputSize: 20,
	};
	const model = {
		fieldKey: FieldStateKey.MODEL,
		label: [Brands.QUALITAS, Brands.INS].includes(BRAND as Brands) ? 'Modelo (Opcional)' : 'Modelo',
		type: 'text',
		readonly: stateKey === IndividualStateKey.INSURED,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.model,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.MODEL),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Modelo requerido',
		inputSize: 20,
	};
	const baseYear = {
		fieldKey: FieldStateKey.YEAR,
		label: [Brands.QUALITAS, Brands.INS].includes(BRAND as Brands) ? 'Año (Opcional)' : 'Año',
		type: 'number',
		required: true,
		inputMode: 'numeric',
		stateSelector: (state: IndividualState): string => state?.fields?.year,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.YEAR),
		isValid: (value: string): boolean =>
			Boolean(value) && String(value).trim().length === 4 && isNum(String(value).trim()),
		errorMessage: 'Año inválido',
	};
	const baseColor = {
		fieldKey: FieldStateKey.COLOR,
		label: [Brands.QUALITAS, Brands.INS].includes(BRAND as Brands) ? 'Color (Opcional)' : 'Color',
		type: 'text',
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.color,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.COLOR),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Color requerido',
		inputSize: 30,
	};
	const email = {
		fieldKey: FieldStateKey.EMAIL,
		label: [Brands.QUALITAS, Brands.INS].includes(BRAND as Brands)
			? 'Correo electrónico (Opcional)'
			: 'Correo electrónico',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.email,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.EMAIL),
		isValid: (value: string): boolean => validateEmail(value),
		errorMessage: 'Correo electrónico inválido',
	};
	const sex = {
		fieldKey: FieldStateKey.SEX,
		label: 'Género',
		type: 'selected',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.sex,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.SEX),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Género es requerido',
		selectedData: selectedSexOptions,
	};
	const license = {
		fieldKey: FieldStateKey.LICENSE,
		label: 'Número de licencia',
		type: 'text',
		inputSize: 13,
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.license,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.LICENSE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Licencia requerida',
	};
	const postalCode = {
		fieldKey: FieldStateKey.POSTAL_CODE,
		label: 'Código Postal',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.postalCode,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.POSTAL_CODE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Código Postal requerido',
	};
	const urbanization = {
		fieldKey: FieldStateKey.URBANIZATION,
		label: 'Urb, Barrio, Edificio o Apartado Postal',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.urbanization,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.URBANIZATION),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Requerido',
	};
	const isOtherDriver = {
		fieldKey: FieldStateKey.IS_OTHER_DRIVER,
		label: 'Marcar el encasillado si el conductor es diferente al dueño registral del vehículo.',
		type: 'checkbox',
		readonly: false,
		required: false,
		stateSelector: (state: IndividualState): string => state?.fields?.isOtherDriver,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.IS_OTHER_DRIVER),
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		isValid: (value: string): boolean => true,
		errorMessage: '',
	};
	const documentType = {
		fieldKey: FieldStateKey.DOCUMENT_TYPE,
		label: 'Tipo de documento',
		type: 'selected',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.documentType,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DOCUMENT_TYPE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Tipo de documento requerido',
		selectedData: selectedDocumentTypeOptions,
	};
	const documentNumber = {
		fieldKey: FieldStateKey.DOCUMENT_NUMBER,
		label: 'Número de documento',
		type: 'numeric',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.documentNumber,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DOCUMENT_NUMBER),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Documento requerido',
	};
	const expeditionPlace = {
		fieldKey: FieldStateKey.EXPEDITION_PLACE,
		label: 'Lugar de expedición',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.expeditionPlace,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.EXPEDITION_PLACE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Lugar de expedición requerido',
	};
	const expeditionDate = {
		fieldKey: FieldStateKey.EXPEDITION_DATE,
		label: 'Fecha de expedición',
		type: 'date',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.expeditionDate,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.EXPEDITION_DATE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Fecha de expedición requerida',
	};
	const billingEmail = {
		fieldKey: FieldStateKey.BILLING_EMAIL,
		label: 'E-mail de facturación electrónica',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.billingEmail,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.BILLING_EMAIL),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'E-mail de Facturación Electrónica requerida',
	};
	const address = {
		fieldKey: FieldStateKey.ADDRESS,
		label: 'Dirección',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.address,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.ADDRESS),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Dirección requerida',
	};
	const insurance = {
		fieldKey: FieldStateKey.INSURANCE,
		label: '¿Su vehículo tiene contratado seguro todo riesgo?',
		type: 'roundButton',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.insurance,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.INSURANCE),
		onClick: buildFieldOnClick(dispatch, stateKey, FieldStateKey.INSURANCE),
		isValid: (value: string | boolean): boolean => value !== undefined && value !== null,
		errorMessage: 'Campo requerida',
	};
	const insuranceName = {
		fieldKey: FieldStateKey.INSURANCE_NAME,
		label: 'En caso de tener, ¿con cuál compañía?',
		type: 'text',
		readonly: false,
		required: false,
		stateSelector: (state: IndividualState): string => state?.fields?.insuranceName,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.INSURANCE_NAME),
		isValid: (): boolean => true,
		errorMessage: '',
	};
	const pep = {
		fieldKey: FieldStateKey.PEP,
		label: '¿Es usted persona políticamente expuesta?',
		type: 'roundButton',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.pep,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.PEP),
		onClick: buildFieldOnClick(dispatch, stateKey, FieldStateKey.PEP),
		isValid: (value: string | boolean): boolean => value !== undefined && value !== null,
		errorMessage: 'Campo requerida',
	};
	const pepLink = {
		fieldKey: FieldStateKey.PEP_LINK,
		label: '¿Existe algún vínculo entre usted y una persona considerada publicamente expuesta?',
		type: 'roundButton',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.pepLink,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.PEP_LINK),
		onClick: buildFieldOnClick(dispatch, stateKey, FieldStateKey.PEP_LINK),
		isValid: (value: string | boolean): boolean => value !== undefined && value !== null,
		errorMessage: 'Campo requerida',
	};
	const linkType = {
		fieldKey: FieldStateKey.LINK_TYPE,
		label: 'Indíque los vínculos existentes',
		type: 'selected',
		readonly: false,
		required: true,
		isHidden: true,
		dependencyKey: FieldStateKey.PEP_LINK,
		stateSelector: (state: IndividualState): string => state?.fields?.linkType,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.LINK_TYPE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Campo requerido',
		selectedData: selectedLinkType,
	};
	const anotherLinkType = {
		fieldKey: FieldStateKey.ANOTHER_LINK_TYPE,
		label: 'Especificar el vínculo',
		type: 'text',
		readonly: false,
		required: false,
		isHidden: true,
		dependencyKey: FieldStateKey.PEP_LINK,
		stateSelector: (state: IndividualState): string => state?.fields?.anotherLinkType,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.ANOTHER_LINK_TYPE),
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		isValid: (value: string): boolean => true,
		errorMessage: '',
	};
	const insuredRelationship = {
		fieldKey: FieldStateKey.INSURED_RELATIONSHIP,
		label: '¿Tiene algún parentesco con el asegurado?',
		type: 'roundButton',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.insuredRelationship,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.INSURED_RELATIONSHIP),
		onClick: buildFieldOnClick(dispatch, stateKey, FieldStateKey.INSURED_RELATIONSHIP),
		isValid: (value: string | boolean): boolean => value !== undefined && value !== null,
		errorMessage: 'Campo requerida',
	};
	const typeOfRelationship = {
		fieldKey: FieldStateKey.TYPE_OF_RELATIONSHIP,
		label: '¿Cúal?',
		type: 'text',
		readonly: false,
		required: false,
		isHidden: true,
		dependencyKey: FieldStateKey.INSURED_RELATIONSHIP,
		stateSelector: (state: IndividualState): string => state?.fields?.typeOfRelationship,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.TYPE_OF_RELATIONSHIP),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Campo requerido',
	};
	const department = {
		fieldKey: FieldStateKey.DEPARTMENT,
		label: 'Departamento',
		type: 'location',
		readonly: false,
		required: false,
		stateSelector: (state: IndividualState): string => state?.fields?.department,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DEPARTMENT),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Departamento requerida',
	};
	// Default: INSURED configuration
	const year = {
		...baseYear,
		readonly: true,
	};

	const color = {
		...baseColor,
		readonly: true,
	};

	const driverName = {
		fieldKey: FieldStateKey.DRIVER_NAME,
		label: 'Nombre (Conductor)',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverName,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_NAME),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Nombre requerido',
	};
	const driverLastName = {
		fieldKey: FieldStateKey.DRIVER_LASTNAME,
		label: 'Apellidos (Conductor)',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverLastName,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_LASTNAME),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Apellidos requeridos',
	};
	const driverPhone = {
		fieldKey: FieldStateKey.DRIVER_PHONE,
		label: 'Teléfono (Conductor)',
		type: 'phone',
		inputMode: 'numeric',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverPhone,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_PHONE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Teléfono requerido',
	};
	const driverMarbete = {
		fieldKey: FieldStateKey.DRIVER_MARBETE,
		label: 'Marbete fecha de vencimiento',
		type: 'date',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverMarbete,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_MARBETE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Marbete requerido',
	};
	const driverMileage = {
		fieldKey: FieldStateKey.DRIVER_MILEAGE,
		label: 'Millaje',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverMileage,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_MILEAGE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Millaje requerido',
	};
	const driverEmail = {
		fieldKey: FieldStateKey.DRIVER_EMAIL,
		label: 'Correo electrónico (Conductor)',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverEmail,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_EMAIL),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Correo electrónico inválido',
	};
	const driverRelationship = {
		fieldKey: FieldStateKey.RELATIONSHIP,
		label: 'Parentesco (Conductor)',
		type: 'text',
		readonly: false,
		required: false,
		stateSelector: (state: IndividualState): string => state?.fields?.driverRelationship,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.RELATIONSHIP),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Parentesco inválido',
	};
	const driverSex = {
		fieldKey: FieldStateKey.DRIVER_SEX,
		label: 'Género (Conductor)',
		type: 'selected',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverSex,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_SEX),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Sexo es requerido',
		selectedData: selectedSexOptions,
	};
	const driverLicense = {
		fieldKey: FieldStateKey.DRIVER_LICENSE,
		label: 'Número de licencia (Conductor)',
		type: 'text',
		inputSize: 10,
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverLicense,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_LICENSE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Licencia requerida',
	};
	const driverPostalCode = {
		fieldKey: FieldStateKey.DRIVER_POSTALCODE,
		label: 'Código Postal (Conductor)',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverPostalCode,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_POSTALCODE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Código Postal requerido',
	};
	const driverUrbanization = {
		fieldKey: FieldStateKey.DRIVER_URBANIZATION,
		label: 'Urb, Barrio, Edificio o Apartado Postal',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverUrbanization,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_URBANIZATION),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Requerido',
	};
	const driverDocumentType = {
		fieldKey: FieldStateKey.DRIVER_DOCUMENT_TYPE,
		label: 'Tipo de documento (Conductor)',
		type: 'selected',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverDocumentType,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_DOCUMENT_TYPE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Tipo de documento requerido',
		selectedData: selectedDocumentTypeOptions,
	};
	const driverDocumentNumber = {
		fieldKey: FieldStateKey.DRIVER_DOCUMENT_NUMBER,
		label: 'Número de documento (Conductor)',
		type: 'numeric',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverDocumentNumber,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_DOCUMENT_NUMBER),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Documento requerido',
	};
	const driverExpeditionPlace = {
		fieldKey: FieldStateKey.DRIVER_EXPEDITION_PLACE,
		label: 'Lugar de expedición (Conductor)',
		type: 'text',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverExpeditionPlace,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_EXPEDITION_PLACE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Lugar de expedición requerido',
	};
	const driverExpeditionDate = {
		fieldKey: FieldStateKey.DRIVER_EXPEDITION_DATE,
		label: 'Fecha de expedición (Conductor)',
		type: 'date',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.driverExpeditionDate,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.DRIVER_EXPEDITION_DATE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Fecha de expedición requerida',
	};
	const brandCatalog = {
		fieldKey: FieldStateKey.MAKE,
		label: 'Marca',
		type: 'selected',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.make,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.MAKE),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Marca requerida',
		selectedData: getBrandsCatalog(),
	};
	const modelCatalog = {
		fieldKey: FieldStateKey.MODEL,
		label: 'Modelo',
		type: 'selected',
		readonly: false,
		required: true,
		stateSelector: (state: IndividualState): string => state?.fields?.model,
		onChange: buildFieldOnChange(dispatch, stateKey, FieldStateKey.MODEL),
		isValid: (value: string): boolean => Boolean(value) && String(value).trim().length > 0,
		errorMessage: 'Modelo requerido',
		selectedData: getModelCatalog(),
	};

	let fields = [firstName, lastName, phone, year, make, model, color, plate, email];
	const isMultiPremOpt =
		BRAND === Brands.MULTINATIONAL || BRAND === Brands.PREMIER || BRAND === Brands.OPTIMA || BRAND === Brands.SODA;
	const isSegurosMundial = BRAND === Brands.SEGUROS_MUNDIAL;

	const screenIndexMap = {
		[Brands.PREMIER]: {
			DRIVER: ScreenIndexPREMIER.DRIVER,
			VEHICLE: ScreenIndexPREMIER.VEHICLE,
		},
		[Brands.OPTIMA]: {
			DRIVER: ScreenIndexOPTIMA.DRIVER,
			VEHICLE: ScreenIndexOPTIMA.VEHICLE,
		},
		[Brands.MULTINATIONAL]: {
			DRIVER: ScreenIndexMultinational.DRIVER,
			VEHICLE: ScreenIndexMultinational.VEHICLE,
		},
		[Brands.SODA]: {
			DRIVER: ScreenIndexSODA.DRIVER,
			VEHICLE: ScreenIndexSODA.VEHICLE,
		},
	};

	const useCPE = BRAND in screenIndexMap;

	const screenIndexDriver = useCPE ? screenIndexMap[BRAND as keyof typeof screenIndexMap].DRIVER : ScreenIndex.DRIVER;
	const screenIndexVehicle = useCPE
		? screenIndexMap[BRAND as keyof typeof screenIndexMap].VEHICLE
		: ScreenIndex.VEHICLE;

	switch (stateKey) {
		case IndividualStateKey.INSURED: {
			if ([Brands.LAFISE, Brands.MNK, Brands.QUALITAS, Brands.ASSA, Brands.PURDY, Brands.INS, Brands.IS_CR].includes(BRAND as Brands)) {
				isOtherDriver.label = 'Marcar el encasillado si el conductor es diferente al dueño registral del vehículo.';
			}

			fields = isMultiPremOpt
				? [firstName, lastName, sex, license, urbanization, postalCode, phone, email, isOtherDriver]
				: isSegurosMundial && (SinisterType === 'Lesiones' || SinisterType === 'Homicidios')
					? [
							firstName,
							lastName,
							documentType,
							documentNumber,
							expeditionPlace,
							expeditionDate,
							department,
							email,
							billingEmail,
							phone,
							insuredRelationship,
							typeOfRelationship,
							pep,
							pepLink,
							linkType,
							anotherLinkType,
						]
					: isSegurosMundial
						? [
								firstName,
								lastName,
								documentType,
								documentNumber,
								expeditionPlace,
								expeditionDate,
								department,
								email,
								billingEmail,
								address,
								phone,
								isOtherDriver,
							]
						: [firstName, lastName, sex, license, phone, email, isOtherDriver];

			switch (screenKey) {
				case screenIndexDriver: {
					fields = [driverName, driverLastName, driverSex, driverLicense, driverPhone, driverEmail];
					if ([Brands.LAFISE, Brands.MNK, Brands.QUALITAS, Brands.ASSA, Brands.PURDY, Brands.INS, Brands.IS_CR].includes(BRAND as Brands)) {
						fields = [
							driverName,
							driverLastName,
							driverSex,
							driverLicense,
							driverPhone,
							driverEmail,
							driverRelationship,
						];
					}
					if ([Brands.MULTINATIONAL, Brands.PREMIER, Brands.OPTIMA, Brands.SODA].includes(BRAND as Brands)) {
						fields = [
							driverName,
							driverLastName,
							driverSex,
							driverLicense,
							driverUrbanization,
							driverPostalCode,
							driverPhone,
							driverEmail,
						];
					}
					if ([Brands.SEGUROS_MUNDIAL].includes(BRAND as Brands)) {
						fields = [
							driverName,
							driverLastName,
							driverDocumentType,
							driverDocumentNumber,
							driverExpeditionPlace,
							driverExpeditionDate,
						];
					}
					break;
				}
				case screenIndexVehicle: {
					if (isMultiPremOpt) {
						plate.label = 'Tablilla';
						plate.errorMessage = 'Tablilla es requerida';
					}
					fields = [plate, year, make, model, color];

					if (isMultiPremOpt) {
						fields.push(driverMarbete, driverMileage);
					}
					const readOnlyFields: Field[] = [plate, year, make, model];
					if ([Brands.SEGUROS_MUNDIAL].includes(BRAND as Brands)) {
						fields = [plate, make, model, year, insurance, insuranceName];
					}
					if (
						[
							Brands.SURA,
							Brands.LAFISE,
							Brands.MNK,
							Brands.QUALITAS,
							Brands.SEGUROS_MUNDIAL,
							Brands.COMFENALCO,
							Brands.MULTINATIONAL,
							Brands.PURDY,
							Brands.INS,
						].includes(BRAND as Brands)
					)
						fields.forEach((f) => {
							f.readonly = readOnlyFields.includes(f);
						});
					break;
				}
				case ScreenIndex.VALIDATION: {
					fields = [pep, pepLink, linkType, anotherLinkType];
					break;
				}
				default:
					break;
			}
			break;
		}
		case IndividualStateKey.INJURED: {
			fields = [];
			firstName.label = 'Nombre del dueño';
			lastName.label = 'Apellido del dueño';
			isOtherDriver.label = 'Marcar el encasillado si el conductor es diferente al dueño registral del vehículo.';
			fields = [firstName, lastName, sex, license, urbanization, postalCode, phone, email, isOtherDriver];

			switch (screenKey) {
				case screenIndexDriver: {
					fields = [];
					if (
						BRAND === Brands.CSM ||
						BRAND === Brands.MULTINATIONAL ||
						BRAND === Brands.PREMIER ||
						BRAND === Brands.SODA
					) {
						fields = [
							driverName,
							driverLastName,
							driverSex,
							driverLicense,
							driverUrbanization,
							driverPostalCode,
							driverPhone,
							driverEmail,
						];
					} else {
						fields = [
							driverName,
							driverLastName,
							driverSex,
							driverLicense,
							driverUrbanization,
							driverPostalCode,
							driverPhone,
							driverEmail,
						];
					}
					break;
				}
				case screenIndexVehicle: {
					fields = [];
					plate.label = 'Tablilla';
					plate.errorMessage = 'Tablilla es requerida';
					fields = [plate, year, make, model, color];

					if (
						BRAND === Brands.CSM ||
						BRAND === Brands.MULTINATIONAL ||
						BRAND === Brands.PREMIER ||
						BRAND === Brands.OPTIMA ||
						BRAND === Brands.SODA
					) {
						fields.push(driverMarbete, driverMileage);
					}

					const readOnlyFields: Field[] = [plate, year, make, model];
					if (
						BRAND === Brands.CSM ||
						BRAND === Brands.MULTINATIONAL ||
						BRAND === Brands.PREMIER ||
						BRAND === Brands.OPTIMA ||
						BRAND === Brands.SODA
					)
						fields.forEach((f) => {
							f.readonly = readOnlyFields.includes(f);
						});
					break;
				}
				default:
					break;
			}
			break;
		}
		case IndividualStateKey.COUNTERPART: {
			year.readonly = false;
			color.readonly = false;
			fields =
				BRAND === Brands.IS_CR
					? [name, phone, make, model, year, color, plate, email]
					: [name, phone, brandCatalog, modelCatalog, year, color, plate, email];

			switch (BRAND) {
				case Brands.SURA: {
					email.required = false;
					break;
				}
				case Brands.LAFISE:
				case Brands.MNK:
				case Brands.PURDY:
				case Brands.QUALITAS:
				case Brands.INS: {
					fields.forEach((f) => (f.required = false));
					break;
				}
				case Brands.SEGUROS_MUNDIAL:
				case Brands.COMFENALCO: {
					email.required = false;
					break;
				}
			}
			break;
		}

		default:
			break;
	}

	return fields;
};

/**
 *
 * @param obj1 Compare OCR Objects
 * @param obj2
 * @returns
 */
export const compareOcrDataAttributes = (
	obj1?: OcrProcessingData | null,
	obj2?: OcrProcessingData | null
): { areIdentical: boolean; differingAttributes: string[] } => {
	const differingAttributes: string[] = [];

	if (obj1 && obj2) {
		for (const key in obj1) {
			if (obj1[key as keyof OcrProcessingData] !== obj2[key as keyof OcrProcessingData]) {
				differingAttributes.push(key);
			}
		}
	}

	return {
		areIdentical: differingAttributes.length === 0,
		differingAttributes,
	};
};
